import * as React from 'react';
import { styled } from '@compiled/react';
import { isCustomPagesUiEnabled, isIncreasedFlexibilityEnabled, isUseHelpCenterRefresh } from 'feature-flags';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';
import { ElementRoot, ElementWidth } from 'view/common/element-root';
import { ElementMargins } from 'view/common/element-root/element-root';
import { HEADER_HEIGHT } from 'view/common/styled';
import { useMediaQuery } from 'view/help-center/use-screen-size/use-screen-size';
import { useIsPageInEditMode } from 'view/layout-next/controllers';
import { Search as SearchV1 } from 'view/search';
import { bp } from 'view/styles/compiled/breakpoints';
import * as mixins from 'view/styles/compiled-mixins';
import * as grid from 'view/styles/grid';
import { sizes } from 'view/styles/sizes-viewport';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { backgroundStyleFromVisualConfig } from '@atlassian/help-center-common-util/bg-style-from-visual-config';
import { useCachedUrl } from '@atlassian/help-center-common-util/use-cached-url';
import { Search } from '@atlassian/help-center-search';
import type { heroElementFragment$key } from './__generated__/heroElementFragment.graphql';
import { BANNER_HEIGHT } from './contants';

export const BANNER_HEIGHT_WITH_HEADER = grid.unitless * 37 + HEADER_HEIGHT; // 352
export const BANNER_HEIGHT_WITHOUT_HEADER = grid.unitless * 37; // 296
export const BANNER_HEIGHT_WITHOUT_SEARCH = grid.unitless * 22; // 176

export interface HeroElementViewerProps {
    heroElement: heroElementFragment$key;
    hasTopBarBeenSplit?: boolean;
    themeColor: string;
    helpCenterTitleColor: string;
    helpCenterBannerUrl?: string;
    hideSearch?: boolean;
    hideTitle?: boolean;
}

const HeroElementBackground = ({
    backgroundColor,
    backgroundImageUrl,
    hasTopBarBeenSplit,
    children,
}: {
    backgroundColor?: string | null;
    backgroundImageUrl?: string | null;
    hasTopBarBeenSplit?: boolean;
    children: React.ReactNode;
}) => {
    di(isIncreasedFlexibilityEnabled);
    if (isIncreasedFlexibilityEnabled()) {
        return (
            <Box
                testId={'hero-element-background'}
                xcss={hasTopBarBeenSplit ? heroElementBackground : heroElementBackgroundWithoutTopBarSplit}
                style={{
                    backgroundColor: backgroundColor ?? undefined,
                    backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : 'inherit',
                }}
            >
                {children}
            </Box>
        );
    }
    return children;
};

export const HeroElement: React.FC<HeroElementViewerProps> = ({
    heroElement,
    hasTopBarBeenSplit,
    themeColor,
    helpCenterTitleColor,
    helpCenterBannerUrl,
    hideSearch = false,
    hideTitle = false,
}) => {
    di(useIsPageInEditMode, isUseHelpCenterRefresh, isIncreasedFlexibilityEnabled);
    // userLanguageTag is not used in this file, but we do need it for update-hero-layout-element.tsx
    /* eslint-disable relay/unused-fields */
    const data = useFragment(
        graphql`
            fragment heroElementFragment on HelpLayoutHeroElement {
                useGlobalSettings
                homePageTitle
                userLanguageTag
                visualConfig {
                    foregroundColor
                    backgroundColor
                    backgroundType
                    backgroundImage {
                        url
                    }
                }
            }
        `,
        heroElement
    );

    let foregroundColor = data.visualConfig?.foregroundColor;
    let backgroundColor: string | null | undefined = undefined;
    let backgroundImageUrl: string | null | undefined = undefined;
    if (isIncreasedFlexibilityEnabled()) {
        foregroundColor = data.useGlobalSettings
            ? helpCenterTitleColor
            : data.visualConfig?.foregroundColor || token('color.text', colors.N800);

        const visualConfigStyle = backgroundStyleFromVisualConfig(data.visualConfig);

        backgroundColor = data.useGlobalSettings ? themeColor : visualConfigStyle.backgroundColor || themeColor;
        backgroundImageUrl = data.useGlobalSettings ? helpCenterBannerUrl : visualConfigStyle.backgroundImage;
    }

    const cachedBackgroundUrl = useCachedUrl(backgroundImageUrl);

    const [isPageInEditMode] = useIsPageInEditMode();
    const { homePageTitle } = data;
    let bannerHeight = BANNER_HEIGHT;
    const isSmallScreenSize = useMediaQuery(`(max-width: ${sizes.xxsmall}px)`);
    if (isUseHelpCenterRefresh()) {
        bannerHeight = hasTopBarBeenSplit ? BANNER_HEIGHT_WITHOUT_HEADER : BANNER_HEIGHT_WITH_HEADER;
    }
    if (isIncreasedFlexibilityEnabled() && hasTopBarBeenSplit) {
        bannerHeight = 300;
    }

    if (isCustomPagesUiEnabled() && hideSearch) {
        bannerHeight = BANNER_HEIGHT_WITHOUT_SEARCH;
    }

    let HelpCenterContainerToRender = HelpCenterContainer;
    if (isUseHelpCenterRefresh()) {
        HelpCenterContainerToRender = hasTopBarBeenSplit
            ? HelpCenterContainerVisualRefresh
            : HelpCenterContainerVisualRefreshWithoutTopBarSplit;
    }
    const titleAlignment = isSmallScreenSize || hideSearch ? 'center' : 'left';

    const renderSearchComponent = () =>
        fg('helpcenter_search_api_changes') ? (
            <Search disableSearch={isPageInEditMode} />
        ) : (
            <SearchV1 disableSearch={isPageInEditMode} />
        );
    return (
        <ElementRoot elementWidth={ElementWidth.FULL_WIDTH} elementMargins={ElementMargins.COMPACT} removePadding>
            <HeroElementBackground
                backgroundColor={backgroundColor}
                backgroundImageUrl={cachedBackgroundUrl}
                hasTopBarBeenSplit={hasTopBarBeenSplit}
            >
                <HelpCenterContainerToRender
                    style={{
                        height: `${bannerHeight}px`,
                    }}
                >
                    {homePageTitle && !hideTitle && (
                        <HelpCenterTitle
                            // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
                            style={{ textAlign: titleAlignment }}
                            titleColor={foregroundColor ?? undefined}
                        >
                            {homePageTitle}
                        </HelpCenterTitle>
                    )}
                    {!hideSearch && renderSearchComponent()}
                </HelpCenterContainerToRender>
            </HeroElementBackground>
        </ElementRoot>
    );
};

const heroElementBackground = xcss({
    position: 'relative',
    width: '100%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    borderRadius: 'border.radius.200',
});

const heroElementBackgroundWithoutTopBarSplit = xcss({
    position: 'relative',
    width: '100%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const HelpCenterContainer = styled.div({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
    ...mixins.borderBox,
    width: '100%',
    margin: '0 auto',
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
    [bp.from.fromXXSmall]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
        maxWidth: `${sizes.small}px`,
        paddingRight: token('space.600', '48px'),
        paddingLeft: token('space.600', '48px'),
    },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    paddingTop: token('space.300', '24px'),
    paddingBottom: token('space.600', '48px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const HelpCenterContainerVisualRefresh = styled.div({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
    ...mixins.borderBox,
    width: '100%',
    margin: '0 auto',
    paddingRight: token('space.200', '16px'),
    paddingLeft: token('space.200', '16px'),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
    [bp.from.fromXXSmall]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
        maxWidth: `${sizes.small}px`,
        paddingRight: token('space.600', '48px'),
        paddingLeft: token('space.600', '48px'),
    },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const HelpCenterContainerVisualRefreshWithoutTopBarSplit = styled.div({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
    ...mixins.borderBox,
    width: '100%',
    margin: '0 auto',
    paddingRight: token('space.200', '16px'),
    paddingLeft: token('space.200', '16px'),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
    [bp.from.fromXXSmall]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
        maxWidth: `${sizes.small}px`,
        paddingRight: token('space.600', '48px'),
        paddingLeft: token('space.600', '48px'),
    },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    paddingTop: token('space.300', '24px'),
    paddingBottom: token('space.600', '48px'),
});

interface HelpCenterTitleProps {
    titleColor?: string;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const HelpCenterTitle = styled.h1<HelpCenterTitleProps>({
    fontSize: '28px', // since we can't use @atlaskit/heading, as we want custom color for heading
    fontWeight: token('font.weight.bold', '600'),
    lineHeight: token('space.400', '32px'),
    letterSpacing: '-0.01em',
    marginBottom: token('space.100', '8px'),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
    color: (props) => (!!props.titleColor ? props.titleColor : token('color.text', colors.N800)),
});
